import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Play, Calendar, Clock, Music } from 'lucide-react';
import axios from 'axios';
import { useSocialLinks } from '../hooks/useSocialLinks';
import { API_URL } from '../utils/apiUrl';
import { formatSeries } from '../utils/formatSeries';
import { extractYouTubeId } from '../utils/extractYouTubeId';
import { SEOHead } from '../components/SEOHead';
import './Episodes.css';

// Helper function to strip HTML tags and clean description text for preview
const stripHtmlTags = (html) => {
  if (!html || typeof html !== 'string') return '';
  
  // Create a temporary element to parse HTML
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  
  // Get text content (strips all HTML tags)
  let text = tmp.textContent || tmp.innerText || '';
  
  // Clean up any remaining artifacts (CSS variables, etc.)
  // Remove any content that looks like CSS (contains --tw- or style definitions)
  if (text.includes('--tw-') || text.includes('style="') || text.includes('class="')) {
    // If text contains CSS artifacts, try to extract just readable text
    text = text.replace(/--[\w-]+:\s*[^;]+;?/g, '').trim();
  }
  
  // Collapse multiple spaces and trim
  return text.replace(/\s+/g, ' ').trim();
};

export const Episodes = () => {
  const [selectedSeries, setSelectedSeries] = useState('all');
  const [episodes, setEpisodes] = useState([]);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Use centralized social links hook
  const { socialLinks, rawData: socialLinksData } = useSocialLinks();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const episodesRes = await axios.get(`${API_URL}/api/cms/episodes`);
      
      // Episodes are already sorted by backend:
      // 1. LIVE episodes (with youtubeId) first, sorted by publishDate DESC (most recent first)
      // 2. UNRELEASED episodes (without youtubeId) last, sorted by publishDate ASC (soonest upcoming first)
      // No additional client-side sorting needed
      setEpisodes(episodesRes.data);
      
      // Extract unique series from episodes (handle both array and legacy string formats)
      const allSeries = episodesRes.data.flatMap(ep => {
        if (Array.isArray(ep.series)) return ep.series;
        return ep.series ? [ep.series] : [];
      });
      const uniqueSeries = [...new Set(allSeries)];
      setSeries(uniqueSeries.map((name, idx) => ({ id: idx, name })));
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter episodes by selected series (handle both array and legacy string formats)
  const filteredEpisodes = selectedSeries === 'all' 
    ? episodes 
    : episodes.filter(ep => {
        if (Array.isArray(ep.series)) {
          return ep.series.includes(selectedSeries);
        }
        return ep.series === selectedSeries;
      });

  return (
    <div className="episodes-page">
      <SEOHead
        title="All Episodes"
        description="Every Golf Nuts conversation, listed in one place. Watch on YouTube, listen on Spotify or Apple Podcasts, and dig into our archive."
        path="/episodes"
      />
      <div className="page-header">
        <div className="container">
          <h1 className="display-medium">All Episodes</h1>
          <p className="body-large">Explore our complete collection of golf stories, conversations, and insights</p>
          
          {/* Social Follow/Subscribe Buttons - Uses centralized social links */}
          {socialLinks.length > 0 && (
            <div className="social-follow-section">
              <p className="follow-text">Follow & Subscribe</p>
              <div className="social-buttons-grid">
                {/* Map through centralized social links */}
                {socialLinks.map((link) => {
                  const IconComponent = link.icon;
                  // Map platform to CSS class
                  const platformClass = {
                    instagram: 'instagram',
                    youtube: 'youtube',
                    x: 'twitter',
                    apple_podcasts: 'apple-podcast',
                    spotify: 'spotify'
                  }[link.platform];
                  
                  // Only show specific platforms in this section
                  if (!['instagram', 'youtube', 'x', 'apple_podcasts', 'spotify'].includes(link.platform)) {
                    return null;
                  }
                  
                  return (
                    <a 
                      key={link.platform}
                      href={link.url}
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={`social-follow-btn ${platformClass || ''}`}
                      aria-label={`Follow on ${link.name}`}
                    >
                      <IconComponent size={20} />
                      <span>{link.name}</span>
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="container">
        {/* Filter Tabs */}
        <div className="series-filter">
          <button 
            className={`filter-btn ${selectedSeries === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedSeries('all')}
          >
            All Episodes
          </button>
          {series.map(s => (
            <button 
              key={s.id}
              className={`filter-btn ${selectedSeries === s.name ? 'active' : ''}`}
              onClick={() => setSelectedSeries(s.name)}
            >
              {s.name}
            </button>
          ))}
        </div>

        {/* Episodes List */}
        <div className="episodes-list">
          {filteredEpisodes.map((episode) => {
            // Extract YouTube ID and determine thumbnail source
            const youtubeId = extractYouTubeId(episode.youtubeId);
            const hasValidYouTube = youtubeId && /^[a-zA-Z0-9_-]{11}$/.test(youtubeId);
            const thumbnailSrc = hasValidYouTube 
              ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`
              : episode.thumbnail;
            
            return (
              <Link to={`/episodes/${episode.slug}`} key={episode.id} className="episode-list-item hover-lift">
                <div className="episode-thumbnail-wrapper">
                  <img 
                    src={thumbnailSrc} 
                    alt={episode.title} 
                    className="episode-thumbnail-img"
                    onError={(e) => {
                      if (episode.thumbnail && e.target.src !== episode.thumbnail) {
                        e.target.src = episode.thumbnail;
                      }
                    }}
                  />
                  <div className="play-button">
                    <Play size={32} color="white" />
                  </div>
                  {hasValidYouTube && (
                    <div className="youtube-indicator">
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="#FF0000">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                    </div>
                  )}
                </div>
                
                <div className="episode-details">
                  <span className="episode-series-tag">{formatSeries(episode.series)}</span>
                  <h2 className="heading-2">{episode.title}</h2>
                  <p className="body-medium episode-description-preview">{stripHtmlTags(episode.description)}</p>
                  
                  <div className="episode-metadata">
                    <span className="metadata-item">
                      <Calendar size={16} />
                      {new Date(episode.publishDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </span>
                    <span className="metadata-item">
                      <Clock size={16} />
                      {episode.duration}
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};
